import { Grid, Paper, Typography, useMediaQuery, useTheme, darken, ButtonBase, Skeleton, Box, CircularProgress, alpha, lighten } from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import getDefaultMemberSearchFormState, { MemberSearchFormState } from 'src/modules/member/models/memberSearchFormState';
import { memberTabs } from 'src/modules/member/models/memberTabs';
import numberWithSpaces from 'src/shared/functions/numberWithSpaces';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { MemberStatusTabKeysEnum } from 'src/shared/models/enums/memberStatusTabKeys.enum';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';

type ColoredBoxProps = {
  bgColor: string;

  isLoading: boolean;
  label: string;
  count: number;
  memberStatus: MemberStatusTabKeysEnum;
};

export const ColoredBox = (props: ColoredBoxProps) => {
  const { bgColor, isLoading, label, count, memberStatus } = props;
  const theme = useTheme();
  const navigate = useNavigate();
  const [loadingGrid, setLoadingGrid] = useState<boolean>(false);

  const isXS = useMediaQuery(theme.breakpoints.down('md'));
  const isMD = useMediaQuery(theme.breakpoints.down('xl'));

  const initialState = getDefaultMemberSearchFormState() as MemberSearchFormState;

  const [advancedSearchFilter, setAdvancedSearchFilter] = useLocalStorage<MemberSearchFormState>(LocalStorageKeys.MemberOverviewFilter, {
    ...initialState,
  });

  const handleClick = useCallback(() => {
    setLoadingGrid(true);
    setAdvancedSearchFilter({ ...advancedSearchFilter, ...{ searchString: '', status: [memberTabs.filter((x) => x.key === memberStatus)[0]] } });
    setTimeout(() => {
      navigate(RoutesEnum.Members);
    }, 1000);
  }, [advancedSearchFilter, memberStatus, navigate, setAdvancedSearchFilter]);

  const currentVariant = (isXS && 'caption') || (isMD && 'body2') || 'body2';
  return (
    <Grid item xs={4}>
      <ButtonBase sx={{ display: 'block', width: '100%', textAlign: 'left' }}>
        <Paper
          component='a'
          onClick={handleClick}
          sx={{
            display: 'block',
            backgroundColor: lighten(bgColor, 0.8),
            color: darken(bgColor, 0.5),
            borderRadius: 2,
            p: 1.5,
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: lighten(bgColor, 0.7),
            },
            position: 'relative',
          }}
          elevation={0}
        >
          {loadingGrid && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(bgColor, 0.7),
                borderRadius: 2,
              }}
            >
              <CircularProgress sx={{ color: 'white' }} size={50} />
            </Box>
          )}
          <Typography sx={{ mb: 2 }} fontWeight={(thm) => thm.typography.fontWeightMedium} variant={currentVariant}>
            {label}
          </Typography>
          {isLoading ? (
            <Skeleton sx={{ fontSize: isXS ? '1rem' : '1.5rem' }} />
          ) : (
            <Typography sx={{ fontSize: isXS ? '1rem' : '1.5rem', fontWeight: (thm) => thm.typography.fontWeightBold }}>
              {numberWithSpaces(count)}
            </Typography>
          )}
        </Paper>
      </ButtonBase>
    </Grid>
  );
};
